import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Divider,
  Pagination,
  IconButton,
  Chip,
  Snackbar
} from '@mui/material';
import {
  Search as SearchIcon,
  Folder as FolderIcon,
  ArrowForward as ArrowForwardIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import GitHubService from '../services/GitHubService';

interface Repository {
  id: number;
  name: string;
  owner: {
    login: string;
  };
  description: string | null; // Updated to allow null values
  html_url: string;
  updated_at: string;
  language: string | null;
  stargazers_count: number;
}

const PER_PAGE = 10;
const STORAGE_KEY = 'translatorx_favorite_repos';
const SELECTED_REPO_KEY = 'translatorx_last_selected_repo';

const filterRepositories = (
  repositories: Repository[],
  searchQuery: string,
  favoriteRepos: number[]
) => {
  const normalizedQuery = searchQuery.trim().toLowerCase();

  const filtered = normalizedQuery
    ? repositories.filter(
        (repo) =>
          repo.name.toLowerCase().includes(normalizedQuery) ||
          repo.owner.login.toLowerCase().includes(normalizedQuery) ||
          (repo.description || '').toLowerCase().includes(normalizedQuery)
      )
    : [...repositories];

  filtered.sort((left, right) => {
    const leftFavorite = favoriteRepos.includes(left.id);
    const rightFavorite = favoriteRepos.includes(right.id);

    if (leftFavorite !== rightFavorite) {
      return leftFavorite ? -1 : 1;
    }

    return new Date(right.updated_at).getTime() - new Date(left.updated_at).getTime();
  });

  return filtered;
};

const RepositorySelection: React.FC = () => {
  const [repositories, setRepositories] = useState<Repository[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedRepo, setSelectedRepo] = useState<Repository | null>(null);
  const [page, setPage] = useState<number>(1);
  const [favoriteRepos, setFavoriteRepos] = useState<number[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const listTopRef = useRef<HTMLDivElement | null>(null);

  const { user, githubConnected } = useAuth();
  const navigate = useNavigate();
  const [githubService, setGithubService] = useState<GitHubService | null>(
    null
  );

  // Initialize GitHub service when user token is available
  useEffect(() => {
    if (user?.token) {
      setGithubService(new GitHubService(user.token));
    } else {
      setGithubService(null);
    }
  }, [user]);

  // Fetch repositories from GitHub
  useEffect(() => {
    const loadFavorites = () => {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        try {
          setFavoriteRepos(JSON.parse(saved));
        } catch (err) {
          console.error('Error parsing favorite repos:', err);
          // Reset if corrupt
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    };

    loadFavorites();
  }, []);

  useEffect(() => {
    const fetchRepositories = async () => {
      if (!githubService) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const repos = await githubService.getRepositories(100);
        setRepositories(repos as Repository[]);
      } catch (err: any) {
        console.error('Error fetching repositories:', err);
        setError('Failed to load repositories: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRepositories();
  }, [githubService]);

  // Toggle favorite status of a repository
  const toggleFavorite = (repoId: number) => {
    let newFavorites: number[];

    if (favoriteRepos.includes(repoId)) {
      // Remove from favorites
      newFavorites = favoriteRepos.filter((id) => id !== repoId);
      setSnackbarMessage('Repository removed from favorites');
    } else {
      // Add to favorites
      newFavorites = [...favoriteRepos, repoId];
      setSnackbarMessage('Repository added to favorites');
    }

    setFavoriteRepos(newFavorites);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newFavorites));
    setSnackbarOpen(true);
  };

  const handleSelectRepository = (repo: Repository) => {
    setSelectedRepo(repo === selectedRepo ? null : repo);

    // Also save last selected repo to localStorage for persistence
    if (repo !== selectedRepo) {
      localStorage.setItem(SELECTED_REPO_KEY, JSON.stringify(repo));
    } else {
      localStorage.removeItem(SELECTED_REPO_KEY);
    }
  };

  const handleContinue = () => {
    if (selectedRepo) {
      navigate(
        `/job/new?owner=${selectedRepo.owner.login}&repo=${selectedRepo.name}`
      );
    }
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.requestAnimationFrame(() => {
      listTopRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  };

  const filteredRepositories = useMemo(
    () => filterRepositories(repositories, searchQuery, favoriteRepos),
    [repositories, searchQuery, favoriteRepos]
  );

  // Load last selected repository from localStorage
  useEffect(() => {
    const loadLastSelectedRepo = () => {
      const saved = localStorage.getItem(SELECTED_REPO_KEY);
      if (saved && repositories.length > 0) {
        try {
          const lastRepo = JSON.parse(saved);
          // Find the repo in our current list
          const matchingRepo = repositories.find(
            (repo) => repo.id === lastRepo.id
          );
          if (matchingRepo) {
            setSelectedRepo(matchingRepo);
          }
        } catch (err) {
          console.error('Error parsing last selected repo:', err);
        }
      }
    };

    if (repositories.length > 0) {
      loadLastSelectedRepo();
    }
  }, [repositories]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredRepositories.length / PER_PAGE));
  const paginatedRepos = useMemo(
    () =>
      filteredRepositories.slice(
        (page - 1) * PER_PAGE,
        page * PER_PAGE
      ),
    [filteredRepositories, page]
  );

  useEffect(() => {
    setPage(1);
  }, [searchQuery, favoriteRepos]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 760,
      }}
    >
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        Select Repository
      </Typography>

      <Alert severity={githubConnected ? 'success' : 'warning'} sx={{ mb: 3 }}>
        {githubConnected ? (
          <>
            GitHub access is connected. Manage or clear it in Settings.
            <Button
              size="small"
              startIcon={<SettingsIcon />}
              sx={{ ml: 1 }}
              onClick={() => navigate('/settings')}
            >
              Open Settings
            </Button>
          </>
        ) : (
          <>
            Connect GitHub access in Settings before selecting a repository.
            <Button
              size="small"
              startIcon={<SettingsIcon />}
              sx={{ ml: 1 }}
              onClick={() => navigate('/settings')}
            >
              Open Settings
            </Button>
          </>
        )}
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        placeholder="Search repositories..."
        variant="outlined"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          )
        }}
        sx={{ mb: 3 }}
        disabled={loading || !githubConnected}
      />

      <Paper
        elevation={2}
        sx={{
          mb: 3,
          width: '100%',
          minWidth: 760,
          maxWidth: 760,
          overflow: 'hidden',
        }}
      >
        <Box ref={listTopRef} />
        {!githubConnected ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography>Connect a GitHub token above to load repositories.</Typography>
          </Box>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredRepositories.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography>No repositories found.</Typography>
          </Box>
        ) : (
          <>
            <List sx={{ width: '100%' }}>
              {paginatedRepos.map((repo, index) => (
                <React.Fragment key={repo.id}>
                  {index > 0 && <Divider />}
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={selectedRepo?.id === repo.id}
                      onClick={() => handleSelectRepository(repo)}
                      sx={{
                        width: '100%',
                        minWidth: 0,
                        alignItems: 'center',
                        pr: 2,
                        '&.Mui-selected': { bgcolor: 'action.selected' },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40, flexShrink: 0 }}>
                        <FolderIcon />
                      </ListItemIcon>
                      <Box
                        sx={{
                          minWidth: 0,
                          flex: '1 1 0',
                          overflow: 'hidden',
                          mr: 1,
                          maxWidth: 'calc(100% - 128px)',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            mb: 0.25,
                          }}
                        >
                          <Typography
                            component="span"
                            fontWeight={
                              selectedRepo?.id === repo.id ? 'bold' : 'normal'
                            }
                            sx={{
                              minWidth: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              flex: '1 1 auto',
                              maxWidth: '100%',
                            }}
                          >
                            {`${repo.owner.login}/${repo.name}`}
                          </Typography>
                          {repo.language && (
                            <Chip
                              label={repo.language}
                              size="small"
                              variant="outlined"
                              sx={{ flexShrink: 0 }}
                            />
                          )}
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%',
                            display: 'block',
                            maxWidth: '100%',
                          }}
                        >
                          {repo.description || 'No description available'}
                        </Typography>
                      </Box>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(repo.id);
                        }}
                        color={
                          favoriteRepos.includes(repo.id) ? 'primary' : 'default'
                        }
                        sx={{ mr: 1, flexShrink: 0 }}
                      >
                        {favoriteRepos.includes(repo.id) ? (
                          <StarIcon />
                        ) : (
                          <StarBorderIcon />
                        )}
                      </IconButton>
                      {selectedRepo?.id === repo.id && (
                        <IconButton
                          color="primary"
                          edge="end"
                          aria-label="selected"
                          sx={{ flexShrink: 0 }}
                        >
                          <ArrowForwardIcon />
                        </IconButton>
                      )}
                    </ListItemButton>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>

            {filteredRepositories.length > PER_PAGE && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          disabled={!selectedRepo}
          onClick={handleContinue}
          endIcon={<ArrowForwardIcon />}
        >
          Continue
        </Button>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default RepositorySelection;
